import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./content.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { persistUserDataIds } from "../../functions/persistUserDataIds";

const HOGWARTS_SELECTION_KEY_BASE = "hogwartsSelectionData";
const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function getSelectionStorageKey(userLike) {
  const stored = readStoredUserData();
  const uid =
    userLike?.userId ?? userLike?.userid ?? userLike?.id ?? stored?.userId ?? stored?.userid ?? stored?.id;
  const sid = userLike?.sessionId ?? stored?.sessionId;
  const oid = userLike?.organizationId ?? stored?.organizationId;
  const scope = [uid, sid, oid].map((v) => String(v ?? "").trim()).join(":");
  return scope.replace(/:/g, "")
    ? `${HOGWARTS_SELECTION_KEY_BASE}:${scope}`
    : HOGWARTS_SELECTION_KEY_BASE;
}

function readSelectionArray(userLike) {
  try {
    const raw = localStorage.getItem(getSelectionStorageKey(userLike));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function normalizePlayerName(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.name;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t && !/^null(\s+null)*$/i.test(t)) return t;
  }
  const fn = user.firstName ?? user.first_name ?? "";
  const ln = user.lastName ?? user.last_name ?? "";
  const parts = [fn, ln].filter(
    (p) =>
      p != null &&
      String(p).trim() !== "" &&
      String(p).trim().toLowerCase() !== "null"
  );
  return parts
    .map((p) => String(p).trim())
    .join(" ")
    .trim();
}

function buildLobbyName() {
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `LOBBY-${rand}`;
}

/**
 * Same folder shape as lobby `CreateFolderSvg` / thank-you feedback — tab top-left, diagonal join.
 * viewBox 0 0 300 240
 */
function ContentFolderSvg() {
  return (
    <svg
      className="content-folder-svg"
      viewBox="0 0 300 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d="
          M12,0
          H148
          L168,36
          H288 Q300,36 300,48
          V228 Q300,240 288,240
          H12 Q0,240 0,228
          V12 Q0,0 12,0 Z
        "
        fill="#ffffff"
      />
    </svg>
  );
}

function resolveLobbyName(stateLike) {
  const stateLobby =
    stateLike && typeof stateLike.lobbyName === "string"
      ? stateLike.lobbyName.trim()
      : "";
  if (stateLobby) {
    try {
      sessionStorage.setItem(CURRENT_LOBBY_KEY, stateLobby);
    } catch {
      // ignore
    }
    return stateLobby;
  }
  try {
    const stored = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (stored && stored.trim()) return stored.trim();
  } catch {
    // ignore
  }
  const generated = buildLobbyName();
  try {
    sessionStorage.setItem(CURRENT_LOBBY_KEY, generated);
  } catch {
    // ignore
  }
  return generated;
}

function formatSecondsToTimer(totalSeconds) {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function parseTimerToSeconds(timerText) {
  const raw = String(timerText ?? "").trim();
  if (!raw) return 0;
  const match = raw.match(/^(\d{1,2}):([0-5]\d)$/);
  if (!match) return 0;
  const minutes = Number(match[1]) || 0;
  const seconds = Number(match[2]) || 0;
  return minutes * 60 + seconds;
}

function sanitizePointsInput(value) {
  return String(value ?? "").replace(/[^\d]/g, "");
}

function sanitizeTimerInput(value) {
  const cleaned = String(value ?? "").replace(/[^\d:]/g, "");
  const parts = cleaned.split(":");
  if (parts.length === 1) {
    return parts[0].slice(0, 2);
  }
  const minutes = parts[0].slice(0, 2);
  const seconds = parts.slice(1).join("").slice(0, 2);
  return `${minutes}:${seconds}`;
}

function Content() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const { status, user } = useSelector((state) => state.auth);

  const [timerEnabled, setTimerEnabled] = useState(false);
  const [questionTimer, setQuestionTimer] = useState("00:00");
  const [questionPoint, setQuestionPoint] = useState("5");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const reportIdRef = useRef(null);
  const stageRowIdRef = useRef(null);

  const storedUserData = useMemo(() => readStoredUserData(), []);
  const authPayload = useMemo(() => {
    const userId =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sessionId = storedUserData.sessionId;
    const organizationId = storedUserData.organizationId;
    const backendBase = String(
      process.env.REACT_APP_BACKEND_URL || ""
    ).replace(/\/+$/, "");
    return { userId, sessionId, organizationId, backendBase };
  }, [storedUserData]);

  useEffect(() => {
    document.documentElement.classList.add("sorthat-static-bg");
    document.body.classList.add("sorthat-static-bg");
    return () => {
      document.documentElement.classList.remove("sorthat-static-bg");
      document.body.classList.remove("sorthat-static-bg");
    };
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/lobby"));
  }, [dispatch, status, user]);

  const sendContentUpdate = useCallback(
    async ({ timerStatus, timerValue, pointValue }) => {
      const { userId, sessionId, organizationId, backendBase } = authPayload;
      if (
        !userId ||
        !sessionId ||
        !organizationId ||
        !backendBase ||
        !adminToken
      ) {
        throw new Error("Missing auth/session details.");
      }
      const res = await fetch(`${backendBase}/updateContentData`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId,
          sessionId,
          organizationId,
          timer_status: timerStatus ? "true" : "false",
          question_timer: timerValue,
          question_point: Number(pointValue) || 0,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Failed to update content settings.");
      }
      return data;
    },
    [adminToken, authPayload]
  );

  useEffect(() => {
    const { userId, sessionId, organizationId, backendBase } = authPayload;
    if (
      !userId ||
      !sessionId ||
      !organizationId ||
      !backendBase ||
      !adminToken
    )
      return;

    const lobbyName = resolveLobbyName(location.state);
    const playerName = normalizePlayerName(storedUserData);
    const roleForApi =
      storedUserData.user_type ||
      storedUserData.userType ||
      storedUserData.role ||
      (storedUserData.source === "DEMO" ? "demobypass" : null) ||
      storedUserData.source ||
      "ORG_USER";

    let cancelled = false;
    setIsLoading(true);
    setErrorMessage("");

    (async () => {
      try {
        const res = await fetch(`${backendBase}/addContent`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId,
            sessionId,
            organizationId,
            email: storedUserData.email || "",
            emp_id:
              storedUserData.employeeId || storedUserData.emp_id || "",
            name: playerName,
            reportId:
              storedUserData.reportId || storedUserData.reportid || "",
            gameId: storedUserData.gameId || "",
            token: storedUserData.token || "",
            user_type: roleForApi,
            lobby: lobbyName,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data?.success) {
          throw new Error(
            data?.message || "Failed to initialize content settings."
          );
        }
        if (data?.id != null && String(data.id).trim() !== "") {
          stageRowIdRef.current = String(data.id).trim();
        }
        if (data?.reportId != null && String(data.reportId).trim() !== "") {
          reportIdRef.current = String(data.reportId).trim();
        }
        persistUserDataIds({
          stageId: stageRowIdRef.current,
          reportId: reportIdRef.current,
          lobby: lobbyName,
        });
        const timerStatus =
          String(data.timer_status || "false").toLowerCase() === "true";
        setTimerEnabled(timerStatus);
        setQuestionTimer(String(data.question_timer || "00:00"));
        const points = Number(data.question_point);
        setQuestionPoint(String(Number.isFinite(points) ? points : 5));
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(
            err?.message || "Unable to load content settings."
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminToken, authPayload, location.state, storedUserData]);

  const handleToggleTimer = async () => {
    const nextValue = !timerEnabled;
    setTimerEnabled(nextValue);
    setErrorMessage("");
    const normalizedTimer = nextValue
      ? formatSecondsToTimer(Math.max(30, parseTimerToSeconds(questionTimer)))
      : "00:00";
    const normalizedPoints = String(Math.max(1, Number(questionPoint) || 0));
    if (nextValue && normalizedTimer !== questionTimer) setQuestionTimer(normalizedTimer);
    if (normalizedPoints !== String(questionPoint)) setQuestionPoint(normalizedPoints);
    try {
      await sendContentUpdate({
        timerStatus: nextValue,
        timerValue: normalizedTimer,
        pointValue: normalizedPoints,
      });
    } catch (err) {
      setTimerEnabled(!nextValue);
      setErrorMessage(err?.message || "Failed to update timer status.");
    }
  };

  const handleNext = async () => {
    if (isSaving) return;
    if (!String(questionPoint).trim()) {
      setErrorMessage("Please enter points per question.");
      return;
    }

    const normalizedPoints = String(Math.max(1, Number(questionPoint) || 0));
    const normalizedTimer = timerEnabled
      ? formatSecondsToTimer(Math.max(30, parseTimerToSeconds(questionTimer)))
      : "00:00";
    if (normalizedPoints !== String(questionPoint)) setQuestionPoint(normalizedPoints);
    if (timerEnabled && normalizedTimer !== questionTimer) setQuestionTimer(normalizedTimer);

    setIsSaving(true);
    setErrorMessage("");
    try {
      await sendContentUpdate({
        timerStatus: timerEnabled,
        timerValue: normalizedTimer,
        pointValue: normalizedPoints,
      });

      const reset = readSelectionArray(user).filter(
        (item) => item?.type === "house"
      );
      localStorage.setItem(
        getSelectionStorageKey(user),
        JSON.stringify(reset)
      );
      navigate("/question", { replace: false });
    } catch (err) {
      setErrorMessage(err?.message || "Failed to save content settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="content-page">
      <div
        className="content-folder"
        role="region"
        aria-labelledby="content-folder-tab-label"
      >
        <ContentFolderSvg />
        {/* Tab label stays in the SVG tab area */}
        {/* <span id="content-folder-tab-label" className="content-folder-tab-text">
          Timer
        </span> */}

        <div className="content-folder-body">
          {/* TIMER heading — now above the ON/OFF toggle inside the card */}
          <p className="content-timer-heading" aria-hidden="true">Timer</p>

          {/* Simple pill ON/OFF toggle */}
          <button
            type="button"
            role="switch"
            aria-checked={timerEnabled}
            className={`content-toggle ${timerEnabled ? "is-on" : "is-off"}`}
            onClick={handleToggleTimer}
            disabled={isLoading || isSaving}
          >
            <span className="content-toggle-text">
              {timerEnabled ? "ON" : "OFF"}
            </span>
          </button>

          {/* Timer per question row — only visible when timer is ON */}
          {timerEnabled && (
            <label className="content-points-row" htmlFor="questionTimerInput">
              <span className="content-points-label">Timer per question :</span>
              <input
                id="questionTimerInput"
                className="content-points-input content-timer-input"
                type="text"
                value={questionTimer}
                inputMode="numeric"
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                }}
                onChange={(e) => setQuestionTimer(sanitizeTimerInput(e.target.value))}
                placeholder="00:00"
                disabled={isLoading || isSaving}
              />
            </label>
          )}

          <label
            className="content-points-row"
            htmlFor="questionPointInput"
          >
            <span className="content-points-label">
              Points per question :
            </span>
            <input
              id="questionPointInput"
              className="content-points-input"
              type="text"
              inputMode="numeric"
              min="0"
              value={questionPoint}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
              }}
              onChange={(e) => setQuestionPoint(sanitizePointsInput(e.target.value))}
              disabled={isLoading || isSaving}
            />
          </label>

          {errorMessage ? (
            <p className="content-error">{errorMessage}</p>
          ) : null}
        </div>
      </div>

      {/* Next button outside the card */}
      <div className="content-next-wrap">
        <button
          type="button"
          className="content-next-btn"
          onClick={handleNext}
          disabled={isLoading || isSaving}
        >
          {isSaving ? "Saving..." : "Next"}
        </button>
      </div>

      <div className="content-loading">
        {isLoading ? "Loading..." : ""}
      </div>
    </div>
  );
}

export default Content;
